import { capitalCase } from "change-case";
import { useMemo } from "react";
import { Schema } from "zod";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import {
  FormCategory,
  formCategoryLabel,
  globalTeamUri,
} from "@smart/bridge-types-basic";
import { loadLocale } from "@smart/itops-locale-dom";
import {
  ContextMenuProps,
  SelectProps,
} from "@smart/itops-sb-design-system-dom";
import { useProviderInfo } from "@smart/itops-smokeball-components-dom";
import { unique } from "@smart/itops-utils-basic";

import { useListState } from "./review-view";
import {
  ReminderMessageFilter,
  categorySchema,
  creatorsSchema,
  filtersSchema,
  listStorageKeys,
  locationsSchema,
  matterTypesSchema,
  reminderMessagesSchema,
} from "./storage-keys";
import { hasCustomReminderMessage } from "../../shared/custom-reminder-message";
import { GqlDeletedForm, GqlForm, GqlSubmissionLinkHash } from "../types";

export type ListFormRow = (GqlForm | GqlDeletedForm) & {
  hasSubmissionLink: boolean;
  submissionLinkHashes: GqlSubmissionLinkHash[];
};

export const useFormData = ({
  forms,
  deletedForms,
  submissionLinkHashes,
  highlightReminderMessage,
}: {
  forms?: GqlForm[];
  deletedForms?: GqlDeletedForm[];
  submissionLinkHashes?: GqlSubmissionLinkHash[];
  highlightReminderMessage?: boolean;
}) => {
  const providerInfo = useProviderInfo();
  const { terms } = loadLocale();
  const { "LINK-3736": automaticRemindersFlag } = useFeatureFlags();
  const [category, setCategory] = useListState("category", {
    defaultValue: undefined,
    key: listStorageKeys.category,
    schema: categorySchema,
    storage: "local",
  });

  const [filters, setFilters] = useListState("filters", {
    defaultValue: [],
    key: listStorageKeys.filters,
    schema: filtersSchema as Schema<(keyof typeof filterSets)[]>,
    storage: "session",
  });
  const [locations, setLocations] = useListState("locations", {
    defaultValue: [],
    key: listStorageKeys.locations,
    schema: locationsSchema,
    storage: "session",
  });
  const [matterTypes, setMatterTypes] = useListState("matterTypes", {
    defaultValue: [],
    key: listStorageKeys.matterTypes,
    schema: matterTypesSchema,
    storage: "session",
  });
  const [creators, setCreators] = useListState("creators", {
    defaultValue: [],
    key: listStorageKeys.creators,
    schema: creatorsSchema,
    storage: "session",
  });
  const [reminderMessages, setReminderMessages] = useListState(
    "reminderMessages",
    {
      defaultValue: [],
      key: listStorageKeys.reminderMessage,
      schema: reminderMessagesSchema,
      storage: "session",
    },
  );

  const byCategory: SelectProps<
    "all" | "deleted" | FormCategory | "webEnquiry"
  > & {
    key: string;
  } = {
    key: "category",
    id: "category",
    name: "category",
    title: "Filter by category",
    options: (["all", "lead", "matter", "webEnquiry", "deleted"] as const).map(
      (o) => {
        const getLabel = () => {
          if (o === "all") return "All Current";
          if (o === "deleted") return "Deleted Forms";
          if (o === "webEnquiry")
            return `Web ${capitalCase(terms.enquiry.plural)}`;
          if (o === "lead") return "Leads";
          if (o === "matter") return "Matters";
          return formCategoryLabel[o];
        };
        return {
          value: o,
          label: getLabel(),
        };
      },
    ),
    value: category || "all",
    onChange: (v) =>
      setCategory(v === "all" || Array.isArray(v) ? undefined : v),
    isMultiple: false,
    className: "w-64",
    dataTestId: "select-category",
  };

  const locationOptions = useMemo(
    () => unique(forms?.flatMap((f) => f.matterTypeLocations) || []).sort(),
    [forms],
  );
  const byLocation: SelectProps<string> & { key: string } = {
    key: "location",
    id: "location",
    name: "location",
    title: `Filter by ${terms.state.name}`,
    options: locationOptions.map((o) => ({ label: o, value: o })),
    value: locations,
    isMultiple: true,
    maxItems: 2,
    onChange: (v) => setLocations(Array.isArray(v) ? v : [v]),
    placeholder: terms.state.title,
    dataTestId: "select-state",
  };

  const matterTypeOptions = useMemo(
    () =>
      unique(
        forms?.flatMap((f) =>
          f.rawForm.values.matterTypes.map((m) => `${m.category}|||${m.name}`),
        ) || [],
      )
        .sort()
        .map((m) => {
          const parts = m.split("|||");
          return { category: parts[0], name: parts[1] };
        }),
    [forms],
  );
  const groupMatterTypeOptions = () => {
    const options: {
      heading: string;
      items: { label: string; value: { name: string; category: string } }[];
    }[] = [];

    matterTypeOptions.forEach((matterType) => {
      const group = options.find((o) => o.heading === matterType.category);
      if (group?.items) {
        group.items.push({ label: matterType.name, value: matterType });
      } else {
        options.push({
          heading: matterType.category,
          items: [{ label: matterType.name, value: matterType }],
        });
      }
    });

    return options;
  };

  const byMatterType: SelectProps<{ name: string; category: string }> & {
    key: string;
  } = {
    key: "matterType",
    id: "matterType",
    name: "matterType",
    title: "Filter by matter type",
    options: groupMatterTypeOptions(),
    value: matterTypes,
    isMultiple: true,
    maxItems: 2,
    onChange: (v) => setMatterTypes(Array.isArray(v) ? v : [v]),
    placeholder: "Types",
    valueComparer: (o, v) => o.name === v.name,
  };

  const creatorOptions = useMemo(
    () =>
      unique(
        forms?.map((f) => f.createdBy || providerInfo?.label || "") || [],
      ).sort(),
    [forms],
  );
  const byCreator: SelectProps<string> & { key: string } = {
    id: "creator",
    name: "creator",
    title: "Filter by creator",
    key: "creator",
    options: creatorOptions.map((o) => ({ label: o, value: o })),
    value: creators,
    isMultiple: true,
    maxItems: 2,
    onChange: (v) => setCreators(Array.isArray(v) ? v : [v]),
    placeholder: "Creators",
    dataTestId: "select-creator",
  };

  const byReminderMessage: SelectProps<ReminderMessageFilter> & {
    key: string;
  } = {
    key: "reminderMessage",
    id: "reminderMessage",
    name: "reminderMessage",
    title: "Filter by reminder message",
    options: [
      { value: "default", label: "Firm default" },
      { value: "custom", label: "Custom" },
    ],
    value: reminderMessages,
    isMultiple: true,
    maxItems: 2,
    onChange: (v) => {
      setReminderMessages(Array.isArray(v) ? v : [v]);
    },
    highlighted: highlightReminderMessage,
    placeholder: "Reminder message",
    dataTestId: "select-reminder-message",
  };

  const filterSets = {
    byLocation,
    byMatterType,
    byCreator,
    byReminderMessage,
  };

  const onToggleFilter = (key: keyof typeof filterSets) => () => {
    if (filters.includes(key)) {
      setFilters((f) => f.filter((k) => k !== key));
      ({
        byLocation: setLocations,
        byMatterType: setMatterTypes,
        byCreator: setCreators,
        byReminderMessage: setReminderMessages,
      })[key]([]);
    } else {
      setFilters((f) => [...f, key]);
    }
  };

  const submissionLinksByFormUri = useMemo(() => {
    const map = new Map<string, GqlSubmissionLinkHash[]>();
    for (const linkHash of submissionLinkHashes ?? []) {
      const existing = map.get(linkHash.formUri);
      if (existing) existing.push(linkHash);
      else map.set(linkHash.formUri, [linkHash]);
    }
    return map;
  }, [submissionLinkHashes]);

  const formUrisWithSubmissionLink = useMemo(
    () => new Set(submissionLinksByFormUri.keys()),
    [submissionLinksByFormUri],
  );

  const filterOptions: ContextMenuProps["items"] = [
    {
      label: `By ${terms.state.name}`,
      checked: filters.includes("byLocation"),
      onClick: onToggleFilter("byLocation"),
    },
    {
      label: "By matter type",
      checked: filters.includes("byMatterType"),
      onClick: onToggleFilter("byMatterType"),
    },
    {
      label: "By creator",
      checked: filters.includes("byCreator"),
      onClick: onToggleFilter("byCreator"),
    },
    ...(automaticRemindersFlag
      ? [
          {
            label: "By reminder message",
            checked: filters.includes("byReminderMessage"),
            onClick: onToggleFilter("byReminderMessage"),
          },
        ]
      : []),
  ];

  const reminderMessageFilter =
    automaticRemindersFlag &&
    category !== "deleted" &&
    filters.includes("byReminderMessage") &&
    reminderMessages.length === 1
      ? reminderMessages[0]
      : undefined;

  const data = useMemo((): ListFormRow[] => {
    const formsToFilter = category === "deleted" ? deletedForms : forms;
    if (!formsToFilter) return [];

    return formsToFilter
      .filter((f) => {
        if (
          category &&
          category !== "deleted" &&
          category !== "webEnquiry" &&
          f.formCategory !== category
        )
          return false;

        if (
          category === "webEnquiry" &&
          !formUrisWithSubmissionLink.has(f.uri)
        ) {
          return false;
        }

        if (creators.length) {
          const isCreator = creators.find((c) => c === f.createdBy);
          const isSmokeball =
            creators.includes(providerInfo?.label || "") &&
            f.teamUri === globalTeamUri;

          if (!isCreator && !isSmokeball) return false;
        }

        if (locations.length) {
          if (
            !locations.find((l) =>
              f.matterTypeLocations.some((mtl) => mtl === l),
            )
          )
            return false;
        }

        if (reminderMessageFilter === "custom") {
          if (!hasCustomReminderMessage(f as GqlForm)) return false;
        }

        if (reminderMessageFilter === "default") {
          if (hasCustomReminderMessage(f as GqlForm)) return false;
        }

        if (matterTypes.length) {
          if (
            !matterTypes.find((mt) =>
              f.rawForm.values.matterTypes.some(
                (m) => m.category === mt.category && m.name === mt.name,
              ),
            )
          )
            return false;
        }

        return true;
      })
      .map((f) => ({
        ...f,
        hasSubmissionLink: formUrisWithSubmissionLink.has(f.uri),
        submissionLinkHashes: submissionLinksByFormUri.get(f.uri) ?? [],
      }));
  }, [
    forms,
    deletedForms,
    category,
    creators,
    locations,
    matterTypes,
    formUrisWithSubmissionLink,
    submissionLinksByFormUri,
    reminderMessageFilter,
  ]);

  return {
    data,
    filterOptions,
    filters: [
      byCategory,
      ...filters
        .filter(
          (f) =>
            f in filterSets &&
            (f !== "byReminderMessage" || automaticRemindersFlag),
        )
        .map((f) => filterSets[f]),
    ] as SelectProps<any>[],
  };
};
